import './styles.css';
import type { GameCommand, GameMessage, GameSettings, SageStackGame } from './gameBridge';
import type { GameMode, GameSnapshot } from './logic/types';
import { piecePreview } from './plantArt';
import { preferences } from './storage';
import { gardenAudio } from './phaser/GardenAudio';
import type { GardenWorld } from './GardenWorld';
import { dailyChallenge, MODE_NAMES, PUZZLES } from './logic/challenges';
import {
  completeRound,
  DECORATIONS,
  emptyRecord,
  purchase,
  readProgress,
  saveProgress,
} from './progression';
import { decorationIcon, gardenIllustration } from './gardenArt';

const el = <T extends HTMLElement = HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing page element: ${selector}`);
  return element;
};
const startButton = el<HTMLButtonElement>('#start-game');
const gameHost = el('#sage-stack-game');
const soundToggle = el<HTMLButtonElement>('#sound-toggle');
const motionToggle = el<HTMLButtonElement>('#motion-toggle');
const waterButton = el<HTMLButtonElement>('#water-button');
const holdButton = el<HTMLButtonElement>('#hold-button');
const pauseButton = el<HTMLButtonElement>('#pause-button');
const help = el<HTMLDialogElement>('#help-dialog');
const overlay = el('#game-overlay');
const fullscreen = el<HTMLButtonElement>('#fullscreen-button');
const controls = [...document.querySelectorAll<HTMLButtonElement>('[data-game-command]')];
let game: SageStackGame | null = null;
let state: GameSnapshot | null = null;
let nextKey = '';
let heldKey = '';
let noticeTimer = 0;
let settings: GameSettings = {
  sound: preferences.getSound(),
  reducedMotion: preferences.getReducedMotion(),
};
const progress = readProgress();
let best = progress.records[preferences.getMode()]?.score ?? 0;
let roundComplete = false;
let lastResult: GameSnapshot | null = null;
const collection = el<HTMLDialogElement>('#collection-dialog');
const puzzleSelect = el<HTMLSelectElement>('#puzzle-select');
let effectTimer = 0;
let music = preferences.getMusic();
let volume = preferences.getVolume();
let scenery = preferences.getScenery();
let haptics = preferences.getHaptics();
let garden: GardenWorld | null = null;
let gardenLoading = false;

async function prepareGarden(): Promise<void> {
  if (!scenery || garden || gardenLoading) return;
  gardenLoading = true;
  try {
    const { GardenWorld } = await import('./GardenWorld');
    if (scenery) {
      garden = new GardenWorld(el('#garden-world'));
      garden.configure(scenery, settings.reducedMotion);
    }
  } catch {
    // Retain the illustrated background when WebGL or the optional chunk is unavailable.
    el('#garden-world').replaceChildren();
    el('#garden-world').classList.remove('is-ready');
    document.documentElement.classList.remove('garden-3d');
  } finally {
    gardenLoading = false;
  }
}

function selectedMode(): GameMode {
  return (document.querySelector<HTMLInputElement>('input[name="game-mode"]:checked')?.value ??
    'growing-season') as GameMode;
}

function persistGarden(): void {
  if (!saveProgress(progress))
    notice('Browser storage is unavailable. Your garden lasts for this visit.');
}
function renderGarden(): void {
  el('#seed-balance').textContent = String(progress.seeds);
  el('#collection-seeds').textContent = String(progress.seeds);
  el('#collection-count').textContent =
    `${progress.owned.length} / ${DECORATIONS.length} collected`;
  el('#garden-summary').textContent = progress.owned.length
    ? `${progress.owned.length} treasures planted. ${progress.seeds} seeds for something new.`
    : 'Gather seeds. Plant something beautiful. Make it yours.';
  el('#garden-vignette').innerHTML = gardenIllustration(progress.equipped, 'vignette');
  el('#collection-preview').innerHTML = gardenIllustration(progress.equipped, 'collection');
  el('#decoration-shop').innerHTML = DECORATIONS.map((item) => {
    const owned = progress.owned.includes(item.id);
    const visible = progress.equipped.includes(item.id);
    return `<button type="button" class="decoration-item ${owned ? 'is-owned' : ''}" data-decoration="${item.id}" ${owned ? `aria-pressed="${visible}"` : progress.seeds < item.cost ? 'disabled' : ''}>${decorationIcon(item.id)}<span><strong>${item.name}</strong><small>${item.detail}</small><b>${owned ? (visible ? 'Planted · tap to hide' : 'Collected · tap to plant') : `${item.cost} seeds`}</b></span></button>`;
  }).join('');
  el('#record-book').innerHTML = (Object.keys(MODE_NAMES) as GameMode[])
    .map((mode) => {
      const r = progress.records[mode] ?? emptyRecord();
      return `<div><strong>${MODE_NAMES[mode]}</strong><span>${r.score.toLocaleString()} best</span><small>${r.rows} rows · ${r.streak}× streak · ${r.blooms} Full Blooms</small></div>`;
    })
    .join('');
  const selected = puzzleSelect.value || '0';
  puzzleSelect.innerHTML = PUZZLES.map(
    (p, i) =>
      `<option value="${i}">${String(i + 1).padStart(2, '0')} · ${p.title}${progress.solved.includes(p.id) ? ' ✓' : ''}</option>`,
  ).join('');
  puzzleSelect.value = selected;
}
function modeDetails(): void {
  const mode = selectedMode();
  const daily = dailyChallenge();
  const puzzle = PUZZLES[Number(puzzleSelect.value)]!;
  el('#quiet-options').hidden = mode !== 'quiet-garden';
  el('#puzzle-options').hidden = mode !== 'puzzle-garden';
  el('#mode-description').textContent =
    mode === 'daily-garden'
      ? `${daily.title} · ${daily.description} New at midnight UTC.`
      : mode === 'puzzle-garden'
        ? puzzle.description
        : mode === 'quiet-garden'
          ? 'No timer. A steady pace. Undo your last planting with U.'
          : mode === 'garden-break'
            ? 'Two minutes to make something beautiful.'
            : 'An endless garden. A little faster every ten rows.';
  best = progress.records[mode]?.score ?? 0;
  el('#high-score').textContent = best.toLocaleString();
  el('#objective-label').textContent = MODE_NAMES[mode].toUpperCase();
  el('#objective').textContent =
    mode === 'daily-garden'
      ? daily.title
      : mode === 'puzzle-garden'
        ? puzzle.title
        : 'Make room to bloom';
}
function roundSettings(): void {
  nextKey = heldKey = '__new-round';
  settings = {
    ...settings,
    quietInterval: Number(el<HTMLSelectElement>('#quiet-speed').value),
    puzzleIndex: Number(puzzleSelect.value),
  };
  preferences.setQuietInterval(settings.quietInterval!);
  preferences.setPuzzleIndex(settings.puzzleIndex!);
  game?.updateSettings(settings);
  roundComplete = false;
  lastResult = null;
  best = progress.records[selectedMode()]?.score ?? 0;
  el('#share-fallback').hidden = true;
}
function updateSettings(): void {
  soundToggle.setAttribute('aria-pressed', String(settings.sound));
  soundToggle.setAttribute('aria-label', `Sound: ${settings.sound ? 'On' : 'Off'}`);
  soundToggle.title = `Sound: ${settings.sound ? 'On' : 'Off'}`;
  soundToggle.textContent = settings.sound ? '♫' : '♪';
  el('#music-toggle').setAttribute('aria-pressed', String(music));
  el('#music-toggle').textContent = `Garden music: ${music ? 'On' : 'Off'}`;
  el('#scenery-toggle').setAttribute('aria-pressed', String(scenery));
  el('#scenery-toggle').textContent = `Garden scenery: ${scenery ? 'On' : 'Off'}`;
  el('#haptics-toggle').setAttribute('aria-pressed', String(haptics));
  el('#haptics-toggle').textContent = `Touch vibration: ${haptics ? 'On' : 'Off'}`;
  el<HTMLInputElement>('#volume-control').value = String(Math.round(volume * 100));
  el('#volume-value').textContent = `${Math.round(volume * 100)}%`;
  gardenAudio.configure(settings.sound, music, volume);
  garden?.configure(scenery, settings.reducedMotion);
  motionToggle.setAttribute('aria-pressed', String(settings.reducedMotion));
  motionToggle.textContent = `Reduced motion: ${settings.reducedMotion ? 'On' : 'Off'}`;
  document.documentElement.classList.toggle('reduced-motion', settings.reducedMotion);
  game?.updateSettings(settings);
}
function notice(message: string): void {
  el('#game-live-status').textContent = message;
  const toast = el('#bloom-notice');
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2300);
}
function updateBest(score: number): void {
  best = Math.max(progress.records[state?.mode ?? selectedMode()]?.score ?? 0, score);
  if (score > preferences.getHighScore()) {
    // Preserve the legacy overall record; the garden book keeps independent mode records.
    preferences.setHighScore(score);
  }
  el('#high-score').textContent = best.toLocaleString();
}
function updateState(snapshot: GameSnapshot): void {
  const previousStatus = state?.status;
  state = snapshot;
  gameHost.dataset.status = snapshot.status;
  document.documentElement.classList.add('mobile-playing');
  el('#objective-label').textContent =
    snapshot.challengeTitle ?? MODE_NAMES[snapshot.mode].toUpperCase();
  el('#objective').textContent =
    snapshot.challengeTarget !== null
      ? `${snapshot.challengeProgress}/${snapshot.challengeTarget} ${snapshot.challengeMetric}${snapshot.piecesRemaining !== null ? ` · ${snapshot.piecesRemaining} left` : ''}`
      : snapshot.mode === 'quiet-garden'
        ? 'A little room to breathe'
        : `${snapshot.fullBlooms} Full Blooms`;
  el('#undo-button').hidden = snapshot.mode !== 'quiet-garden';
  document.documentElement.classList.toggle('quiet-playing', snapshot.mode === 'quiet-garden');
  el('#score').textContent = snapshot.score.toLocaleString();
  el('#rows').textContent = String(snapshot.lines);
  el('#streak').textContent = String(snapshot.bloomStreak);
  el('#level-label').textContent =
    snapshot.mode === 'quiet-garden'
      ? 'PACE'
      : snapshot.mode === 'puzzle-garden'
        ? 'PIECES'
        : snapshot.timeRemainingMs === null
          ? 'SEASON'
          : 'TIME';
  const seconds = Math.ceil((snapshot.timeRemainingMs ?? 0) / 1000);
  el('#level').textContent =
    snapshot.mode === 'quiet-garden'
      ? settings.quietInterval === 2500
        ? 'Slow'
        : settings.quietInterval === 850
          ? 'Steady'
          : 'Easy'
      : snapshot.mode === 'puzzle-garden'
        ? String(snapshot.piecesRemaining)
        : snapshot.timeRemainingMs === null
          ? String(snapshot.level).padStart(2, '0')
          : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  updateBest(snapshot.score);
  const key = snapshot.nextPieces.join('');
  if (key !== nextKey) {
    nextKey = key;
    el('#next-pieces').innerHTML = snapshot.nextPieces.length
      ? snapshot.nextPieces.map(piecePreview).join('')
      : '<small class="last-piece">Last piece</small>';
  }
  const held = snapshot.heldPiece ?? '';
  if (held !== heldKey) {
    heldKey = held;
    el('#held-piece').innerHTML = snapshot.heldPiece
      ? piecePreview(snapshot.heldPiece)
      : '<span class="empty-hold">＋</span>';
  }
  const playing = snapshot.status === 'playing';
  controls.forEach((button) => {
    const command = button.dataset.gameCommand;
    button.disabled =
      command === 'pause'
        ? !['playing', 'paused'].includes(snapshot.status)
        : !playing ||
          (command === 'hold' && !snapshot.canHold) ||
          (command === 'water' && !snapshot.wateringCan) ||
          (command === 'undo' && !snapshot.canUndo);
  });
  holdButton.title = snapshot.canHold
    ? 'Save or swap a piece (C)'
    : 'Plant this piece before holding again';
  el('#hold-hint').textContent = snapshot.canHold ? 'Save for later' : 'Plant to unlock';
  waterButton.classList.toggle('is-ready', snapshot.wateringCan);
  if (!snapshot.wateringCan) waterButton.setAttribute('aria-pressed', 'false');
  el('#water-fill').style.width =
    `${snapshot.wateringCan ? 100 : (snapshot.wateringProgress / 8) * 100}%`;
  el('#water-progress').textContent = snapshot.wateringCan
    ? 'READY · TAP'
    : `${snapshot.wateringProgress} / 8 rows`;
  pauseButton.setAttribute(
    'aria-label',
    snapshot.status === 'paused' ? 'Resume game' : 'Pause game',
  );
  pauseButton.textContent = snapshot.status === 'paused' ? '▷' : 'Ⅱ';
  document.querySelectorAll<HTMLInputElement>('input[name="game-mode"]').forEach((input) => {
    input.disabled = snapshot.status !== 'over';
  });
  if (!playing) stopRepeating();
  if (snapshot.status === 'paused' && previousStatus !== 'paused') {
    overlay.hidden = false;
    el('#overlay-eyebrow').textContent = 'TAKE A BREATHER';
    el('#overlay-title').innerHTML = 'The garden<br />is resting.';
    el('#overlay-description').textContent = 'Your plants will wait for you.';
    el('#result-stats').hidden = true;
    el('#resume-game').hidden = false;
    el('#plant-again').hidden = true;
    el('#finish-round').hidden = false;
    el('#change-mode').hidden = true;
    el('#share-result').hidden = true;
    el('#next-puzzle').hidden = true;
    el('#share-fallback').hidden = true;
    el('#game-live-status').textContent = 'The garden is paused.';
    if (!help.open && !collection.open) el('#resume-game').focus({ preventScroll: true });
  } else if (playing) {
    overlay.hidden = true;
    if (previousStatus === 'paused') el('#game-live-status').textContent = 'Planting resumed.';
  }
}
function handleMessage(message: GameMessage): void {
  if (message.type === 'ready') {
    el('#loading-status').hidden = true;
    gameHost.dataset.gameReady = 'true';
    gameHost.focus({ preventScroll: true });
    if (help.open || collection.open || document.hidden) game?.pauseForVisibility();
  } else if (message.type === 'garden-effect') {
    if (!settings.reducedMotion && scenery) {
      document.documentElement.dataset.gardenEffect = message.effect;
      window.clearTimeout(effectTimer);
      effectTimer = window.setTimeout(
        () => delete document.documentElement.dataset.gardenEffect,
        2200,
      );
    }
  } else if (message.type === 'state') updateState(message.snapshot);
  else if (message.type === 'score') updateBest(message.score);
  else if (message.type === 'status') notice(message.message);
  else if (message.type === 'results') {
    if (roundComplete) return;
    roundComplete = true;
    lastResult = message.snapshot;
    const reward = completeRound(progress, message.snapshot);
    persistGarden();
    renderGarden();
    updateBest(message.score);
    overlay.hidden = false;
    el('#overlay-eyebrow').textContent = reward.newBest
      ? 'A NEW PERSONAL BLOOM'
      : 'EVERY GARDEN IS A NEW BEGINNING';
    el('#overlay-title').innerHTML =
      message.reason === 'challenge-complete'
        ? 'Beautifully<br /><em>grown.</em>'
        : message.reason === 'pieces-used'
          ? 'A fresh<br /><em>approach?</em>'
          : message.reason === 'garden-ended' || message.reason === 'season-ended'
            ? 'A little time<br /><em>well spent.</em>'
            : 'Room for a<br /><em>fresh start.</em>';
    el('#overlay-description').textContent =
      `${reward.seeds ? `+${reward.seeds} seeds for your garden. ` : ''}${message.reason === 'challenge-complete' ? (reward.firstSolve ? 'Challenge complete. A new little milestone.' : 'Another lovely solve.') : message.snapshot.mode === 'puzzle-garden' ? 'Try a different placement. Each piece has a place.' : reward.newBest ? `Your best ${MODE_NAMES[message.snapshot.mode]} yet.` : `${Math.max(0, best - message.score).toLocaleString()} points from your mode record.`}`;
    el('#resume-game').hidden = true;
    el('#plant-again').hidden = false;
    el('#finish-round').hidden = true;
    el('#change-mode').hidden = false;
    el('#share-result').hidden = message.snapshot.mode !== 'daily-garden';
    el('#next-puzzle').hidden =
      message.snapshot.mode !== 'puzzle-garden' ||
      message.reason !== 'challenge-complete' ||
      Number(puzzleSelect.value) >= PUZZLES.length - 1;
    const stats = el('#result-stats');
    stats.hidden = false;
    stats.innerHTML = [
      [message.score.toLocaleString(), 'SCORE'],
      [message.lines, 'ROWS'],
      [message.bestStreak, 'BEST STREAK'],
      [message.snapshot.fullBlooms, 'FULL BLOOMS'],
    ]
      .map(([value, label]) => `<div><strong>${value}</strong><small>${label}</small></div>`)
      .join('');
    el('#game-live-status').textContent =
      `Garden complete. Score ${message.score}, ${message.lines} rows planted.`;
    if (!help.open && !collection.open) el('#plant-again').focus({ preventScroll: true });
  }
}

async function startGame(): Promise<void> {
  roundSettings();
  gardenAudio.unlock();
  startButton.disabled = true;
  el('#loading-status').hidden = false;
  el('#game-error').hidden = true;
  preferences.setMode(selectedMode());
  try {
    const { createSageStackGame } = await import('./phaser/createSageStackGame');
    document.documentElement.classList.add('mobile-playing');
    el('#game-welcome').hidden = true;
    gameHost.hidden = false;
    el('#game-frame').classList.add('is-active');
    game = createSageStackGame({
      parent: gameHost,
      mode: selectedMode(),
      settings,
      onMessage: handleMessage,
    });
    preferences.setInstructionsViewed();
  } catch (error) {
    console.error('Sage Stack failed to start.', error);
    game?.destroy();
    game = null;
    gameHost.hidden = true;
    document.documentElement.classList.remove('mobile-playing');
    el('#game-frame').classList.remove('is-active');
    el('#loading-status').hidden = true;
    el('#game-error').hidden = false;
    el('#game-welcome').hidden = false;
    startButton.disabled = false;
  }
}
startButton.addEventListener('click', () => void startGame());
document.querySelectorAll<HTMLInputElement>('input[name="game-mode"]').forEach((input) => {
  input.checked = input.value === preferences.getMode();
  input.addEventListener('change', () => {
    preferences.setMode(selectedMode());
    modeDetails();
    if (state?.status === 'over') notice('Mode selected. Plant again for a fresh garden.');
  });
});
el('#plant-again').addEventListener('click', () => {
  gardenAudio.unlock();
  roundSettings();
  game?.restart(selectedMode());
  gameHost.focus({ preventScroll: true });
});
el('#next-puzzle').addEventListener('click', () => {
  puzzleSelect.value = String(Math.min(PUZZLES.length - 1, Number(puzzleSelect.value) + 1));
  roundSettings();
  game?.restart('puzzle-garden');
  gameHost.focus({ preventScroll: true });
});
el('#finish-round').addEventListener('click', () => game?.command('finish'));
el('#change-mode').addEventListener('click', () => {
  game?.destroy();
  game = null;
  state = null;
  nextKey = heldKey = '';
  overlay.hidden = true;
  gameHost.hidden = true;
  gameHost.removeAttribute('data-game-ready');
  el('#game-welcome').hidden = false;
  el('#game-frame').classList.remove('is-active');
  document.documentElement.classList.remove('mobile-playing', 'quiet-playing');
  startButton.disabled = false;
  el('#score').textContent = '0';
  el('#rows').textContent = '0';
  el('#level').textContent = '01';
  el('#next-pieces').innerHTML = '<span class="empty-hold">✳</span>';
  el('#held-piece').innerHTML = '<span class="empty-hold">＋</span>';
  controls.forEach((button) => {
    button.disabled = true;
  });
  document.querySelectorAll<HTMLInputElement>('input[name="game-mode"]').forEach((input) => {
    input.disabled = false;
  });
  modeDetails();
  startButton.focus({ preventScroll: true });
});
puzzleSelect.addEventListener('change', modeDetails);
el('#share-result').addEventListener('click', () => {
  if (!lastResult) return;
  const s = lastResult;
  const text = `Sage Stack · Daily Garden · ${s.challengeId?.replace('daily-', '')}\n${s.challengeTitle} · ${s.endReason === 'challenge-complete' ? 'Solved ✿' : `${s.challengeProgress}/${s.challengeTarget}`}\n${s.score.toLocaleString()} points · ${s.lines} rows · ${s.fullBlooms} Full Blooms\nhttps://thegardensage.net/sage-stack/`;
  const fallback = (): void => {
    const field = el<HTMLTextAreaElement>('#share-fallback');
    field.hidden = false;
    field.value = text;
    field.focus();
    field.select();
  };
  if (navigator.clipboard?.writeText)
    void navigator.clipboard
      .writeText(text)
      .then(() => notice('Daily result copied. Ready to share.'))
      .catch(fallback);
  else fallback();
});
function openGarden(): void {
  stopRepeating();
  game?.pauseForVisibility();
  renderGarden();
  document.documentElement.classList.add('help-open');
  collection.showModal();
}
el('#garden-button').addEventListener('click', openGarden);
el('#visit-garden').addEventListener('click', openGarden);
el('#close-collection').addEventListener('click', () => collection.close());
collection.addEventListener('close', () => {
  document.documentElement.classList.remove('help-open');
  if (state?.status === 'paused') el('#resume-game').focus({ preventScroll: true });
});
el('#decoration-shop').addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-decoration]');
  const id = button?.dataset.decoration;
  if (!id || button?.disabled) return;
  if (progress.owned.includes(id)) {
    progress.equipped = progress.equipped.includes(id)
      ? progress.equipped.filter((item) => item !== id)
      : [...progress.equipped, id];
  } else if (!purchase(progress, id)) return;
  persistGarden();
  renderGarden();
  el('#collection-status').textContent =
    `${DECORATIONS.find((d) => d.id === id)!.name} ${progress.equipped.includes(id) ? 'planted in your garden.' : 'stored safely in your collection.'}`;
  el<HTMLButtonElement>(`[data-decoration="${id}"]`).focus({ preventScroll: true });
});
el('#resume-game').addEventListener('click', () => {
  gardenAudio.unlock();
  game?.command('pause');
  gameHost.focus({ preventScroll: true });
});
soundToggle.addEventListener('click', () => {
  settings = { ...settings, sound: !settings.sound };
  preferences.setSound(settings.sound);
  updateSettings();
  gardenAudio.unlock();
});
el('#music-toggle').addEventListener('click', () => {
  music = !music;
  preferences.setMusic(music);
  updateSettings();
  gardenAudio.unlock();
});
el('#scenery-toggle').addEventListener('click', () => {
  scenery = !scenery;
  preferences.setScenery(scenery);
  updateSettings();
  void prepareGarden();
});
el('#haptics-toggle').hidden = !('vibrate' in navigator);
el('#haptics-toggle').addEventListener('click', () => {
  haptics = !haptics;
  preferences.setHaptics(haptics);
  updateSettings();
  if (haptics) navigator.vibrate?.(12);
});
el('#volume-control').addEventListener('input', (event) => {
  volume = Number((event.target as HTMLInputElement).value) / 100;
  preferences.setVolume(volume);
  updateSettings();
});
motionToggle.addEventListener('click', () => {
  settings = { ...settings, reducedMotion: !settings.reducedMotion };
  preferences.setReducedMotion(settings.reducedMotion);
  updateSettings();
});
el('#help-button').addEventListener('click', () => {
  stopRepeating();
  game?.pauseForVisibility();
  document.documentElement.classList.add('help-open');
  help.showModal();
});
el('#close-help').addEventListener('click', () => help.close());
help.addEventListener('close', () => {
  document.documentElement.classList.remove('help-open');
  if (state?.status === 'paused') el('#resume-game').focus({ preventScroll: true });
});
fullscreen.hidden = !document.fullscreenEnabled;
fullscreen.addEventListener('click', () => {
  const action = document.fullscreenElement
    ? document.exitFullscreen()
    : document.documentElement.requestFullscreen();
  void action.catch(() => notice('Full screen is unavailable in this browser.'));
});
document.addEventListener('fullscreenchange', () =>
  fullscreen.setAttribute(
    'aria-label',
    document.fullscreenElement ? 'Exit full screen' : 'Enter full screen',
  ),
);

let repeatDelay = 0;
let repeatTimer = 0;
let heldButton: HTMLButtonElement | null = null;
let heldPointerId: number | null = null;
function stopRepeating(): void {
  window.clearTimeout(repeatDelay);
  window.clearInterval(repeatTimer);
  heldButton?.classList.remove('is-held');
  heldButton = null;
  heldPointerId = null;
}
function command(button: HTMLButtonElement): void {
  gardenAudio.unlock();
  if (haptics && state?.status === 'playing')
    navigator.vibrate?.(button.dataset.gameCommand === 'drop' ? 18 : 7);
  game?.command(button.dataset.gameCommand as GameCommand);
  if (button === waterButton && state?.wateringCan)
    waterButton.setAttribute(
      'aria-pressed',
      String(waterButton.getAttribute('aria-pressed') !== 'true'),
    );
}
controls.forEach((button) => {
  button.addEventListener('pointerdown', (event) => {
    if (button.disabled || event.button !== 0) return;
    event.preventDefault();
    const repeats = ['left', 'right', 'down'].includes(button.dataset.gameCommand ?? '');
    if (repeats) stopRepeating();
    button.setPointerCapture(event.pointerId);
    command(button);
    if (repeats) {
      heldButton = button;
      heldPointerId = event.pointerId;
      button.classList.add('is-held');
      repeatDelay = window.setTimeout(() => {
        repeatTimer = window.setInterval(
          () => {
            if (state?.status !== 'playing' || button.disabled) stopRepeating();
            else command(button);
          },
          button.dataset.gameCommand === 'down' ? 42 : 65,
        );
      }, 180);
    }
    if (state?.status === 'playing') gameHost.focus({ preventScroll: true });
  });
  for (const event of ['pointerup', 'pointercancel', 'lostpointercapture'])
    button.addEventListener(event, (event) => {
      if ((event as PointerEvent).pointerId === heldPointerId) stopRepeating();
    });
  button.addEventListener('click', (event) => {
    if (event.detail === 0 && !button.disabled) command(button);
  });
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    stopRepeating();
    game?.pauseForVisibility();
  }
});
window.addEventListener('blur', () => {
  stopRepeating();
  game?.pauseForVisibility();
});
window.addEventListener('beforeunload', () => {
  game?.destroy();
  garden?.destroy();
  gardenAudio.destroy();
});
el('#welcome-art').innerHTML = piecePreview('T');
el('#next-pieces').innerHTML = '<span class="empty-hold">✳</span>';
const playArea = el('.play-area');
const fitBoard = (): void => {
  const { width, height } = playArea.getBoundingClientRect();
  if (!width || !height) return;
  const small = window.innerWidth <= 760;
  const landscape = window.innerHeight <= 500 && window.innerWidth >= 600;
  const tray = landscape ? 72 : small ? 66 : 96;
  const gap = small ? 7 : 10;
  const potentialBoardWidth = Math.min(
    ((el('.game-column').clientHeight - el('#touch-controls').offsetHeight - 8) * 344) / 664,
    width - tray - gap,
  );
  document.documentElement.classList.toggle(
    'stats-side',
    window.innerWidth >= 1000 &&
      !landscape &&
      (width - potentialBoardWidth - tray - gap) / 2 >= 170,
  );
  const sideScale = Math.min((width - tray - gap) / 344, height / 664);
  const belowScale = Math.min(width / 344, (height - 60) / 664);
  const below = small && !landscape && belowScale > sideScale;
  const scale = Math.max(0.1, below ? belowScale : sideScale);
  playArea.classList.toggle('tray-below', below);
  el('.game-column').style.setProperty('--bed-width', `${Math.floor(344 * scale)}px`);
  el('.game-column').style.setProperty('--bed-height', `${Math.floor(664 * scale)}px`);
};
new ResizeObserver(fitBoard).observe(playArea);
window.addEventListener('resize', fitBoard);
fitBoard();
updateSettings();
renderGarden();
puzzleSelect.value = String(preferences.getPuzzleIndex());
el<HTMLSelectElement>('#quiet-speed').value = String(preferences.getQuietInterval());
modeDetails();
updateBest(best);
window.setTimeout(() => void prepareGarden(), 400);
